import { toSentenceCase } from '@omnipresentgroup/helpers';
import { DocumentType } from '@omnipresentgroup/types';
import type { JSONSchema7, JSONSchema7Definition } from 'json-schema';
import { useEffect, useState } from 'react';

import type { DocumentFormFields } from '../types';

const getArraySchemaFromEnum = (
  enumObject: object,
  transformTitle = true,
): JSONSchema7Definition[] =>
  Object.values(enumObject).map((type) => ({
    const: type,
    title: transformTitle ? toSentenceCase(type) : type,
  }));

export const useDocumentSchema = ({
  formValues,
  allowAddContract,
}: {
  formValues?: DocumentFormFields;
  allowAddContract?: boolean;
}) => {
  const [schema, setSchema] = useState<JSONSchema7>({});
  const [uiSchema, setUiSchema] = useState({});
  useEffect(() => {
    setSchema({
      type: 'object',
      required: ['title', 'document', ...(allowAddContract ? ['type'] : [])],
      properties: {
        title: {
          type: 'string',
          title: 'Title',
        },
        description: {
          type: 'string',
          title: 'Description of the document',
        },
        ...(allowAddContract
          ? {
              type: {
                type: 'string',
                title: 'Type of the document',
                oneOf: getArraySchemaFromEnum(DocumentType),
              },
            }
          : {}),
        ...(formValues?.type === DocumentType.CONTRACT
          ? {
              version: {
                type: 'number',
                title: 'Version of contract',
              },
              status: {
                type: 'string',
                title: 'Status of contract',
                oneOf: [
                  { const: 'draft', title: 'Draft' },
                  {
                    const: 'pending-internal-review',
                    title: 'Approved by manager',
                  },
                  { const: 'approved', title: 'Internal review completed' },
                  {
                    const: 'signed:employee',
                    title: 'Signed by employee only',
                  },
                  {
                    const: 'signed:both-parties',
                    title: 'Signed by both parties',
                  },
                ],
              },
            }
          : {}),
        document: {
          type: 'string',
          format: 'data-url',
          title: 'Document',
        },
      },
    });

    setUiSchema({
      'ui:submitButtonOptions': {
        norender: true,
      },
      document: {
        'ui:options': {
          dropzoneOptions: {
            accept:
              formValues?.type === DocumentType.CONTRACT
                ? {
                    'application/pdf': ['.pdf'],
                  }
                : {
                    'image/jpeg': ['.jpeg'],
                    'image/png': ['.png'],
                    'application/pdf': ['.pdf'],
                    'application/msword': ['.doc'],
                    'text/csv': ['.csv'],
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                      ['.docx'],
                    'application/vnd.ms-powerpoint': ['.ppt'],
                    'application/vnd.openxmlformats-officedocument.presentationml.presentation':
                      ['.pptx'],
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                      ['.xlsx'],
                    'application/rtf': ['.rtf'],
                    'text/plain': ['.txt'],
                    'application/vnd.ms-excel': ['.xls'],
                  },
          },
          fileInfo:
            formValues?.type === DocumentType.CONTRACT
              ? 'You can upload one PDF file'
              : 'You can upload one file',
        },
      },
    });
  }, [formValues, allowAddContract]);

  return {
    isLoading: false,
    schema,
    uiSchema,
  };
};
